<template>
  <el-table :data="tableData" stripe border show-overflow-tooltip max-height="650">
    <el-table-column type="expand">
      <template #default="group">
        <el-table :data="group.row.family" stripe border show-overflow-tooltip max-height="650">
          <template v-if="group.row.source === 0">
            <el-table-column prop="score" label="分值" min-width="100" align="center" />
            <el-table-column prop="schedTime" label="时间" min-width="160" align="center" />

            <el-table-column prop="checkers" label="排查者" min-width="120" align="center">
              <template #default="child">
                <template v-for="(uid, index) in child.row.checkers" :key="index">
                  <span v-if="index !== 0" class="delim">,</span>
                  <UserInfoPopover :user-map="userMap" :arch-map="archMap" :user-id="uid" />
                </template>
              </template>
            </el-table-column>

            <el-table-column label="任务详情" min-width="120" align="center">
              <template #default="child">
                <el-link type="primary" :underline="false" icon="Position" @click="$router.push({ path: '/core/check/checkTask/look', query: { id: child.row.sourceId } })">前往</el-link>
              </template>
            </el-table-column>
          </template>

          <template v-if="group.row.source === 1">
            <el-table-column prop="score" label="分值" min-width="120" align="center" />
            <el-table-column prop="schedTime" label="时间" min-width="160" align="center" />

            <el-table-column prop="checkers" label="排查者" min-width="120" align="center">
              <template #default="child">
                <UserInfoPopover :user-map="userMap" :arch-map="archMap" :user-id="child.row.checker" />
              </template>
            </el-table-column>

            <el-table-column label="任务详情" min-width="120" align="center">
              <template #default="child">
                <el-link type="primary" :underline="false" icon="Position" @click="$router.push({ path: '/core/check/riskTask/look', query: { id: child.row.taskId } })">前往</el-link>
              </template>
            </el-table-column>
          </template>

          <template v-if="group.row.source === 2">
            <el-table-column prop="score" label="分值" min-width="120" align="center" />
            <el-table-column prop="time" label="时间" min-width="160" align="center" />

            <el-table-column prop="userId" label="参加者" min-width="120" align="center">
              <template #default="child">
                <UserInfoPopover :user-map="userMap" :arch-map="archMap" :user-id="child.row.userId" />
              </template>
            </el-table-column>

            <el-table-column label="培训详情" min-width="120" align="center">
              <template #default="child">
                <el-link type="primary" :underline="false" icon="Position" @click="$router.push({ path: '/trainExam/onlineTrain/risk/look', query: { id: child.row.trainId } })">前往</el-link>
              </template>
            </el-table-column>
          </template>

          <template v-if="group.row.source === 3">
            <el-table-column prop="score" label="分值" min-width="120" align="center" />
            <el-table-column prop="time" label="时间" min-width="160" align="center" />

            <el-table-column prop="submitter" label="提交者" min-width="120" align="center">
              <template #default="child">
                <UserInfoPopover :user-map="userMap" :arch-map="archMap" :user-id="child.row.submitter" />
              </template>
            </el-table-column>

            <el-table-column prop="headId" label="负责人" min-width="120" align="center">
              <template #default="child">
                <UserInfoPopover :user-map="userMap" :arch-map="archMap" :user-id="child.row.headId" />
              </template>
            </el-table-column>

            <el-table-column label="安全隐患详情" min-width="120" align="center">
              <template #default="child">
                <el-link type="primary" :underline="false" icon="Position" @click="$router.push({ path: '/core/hazard/look', query: { id: child.row.sourceId } })">前往</el-link>
              </template>
            </el-table-column>
          </template>

          <template v-if="group.row.source === 4">
            <el-table-column prop="score" label="分值" min-width="120" align="center" />
            <el-table-column prop="time" label="时间" min-width="160" align="center" />

            <el-table-column prop="submitter" label="票据类型" min-width="120" align="center">
              <template #default="child">
                <el-text>{{ ticketTypeMap[child.row.type].name }}</el-text>
              </template>
            </el-table-column>

            <el-table-column prop="drawer" label="开票人" min-width="120" align="center">
              <template #default="child">
                <UserInfoPopover :user-map="userMap" :arch-map="archMap" :user-id="child.row.drawer" />
              </template>
            </el-table-column>

            <el-table-column prop="reviewer" label="审批者" min-width="120" align="center">
              <template #default="child">
                <UserInfoPopover :user-map="userMap" :arch-map="archMap" :user-id="child.row.reviewer" />
              </template>
            </el-table-column>

            <el-table-column label="票据详情" min-width="120" align="center">
              <template #default="child">
                <el-link type="primary" :underline="false" icon="Position" @click="$router.push({ path: ticketTypeMap[child.row.type].url, query: { id: child.row.sourceId } })">前往</el-link>
              </template>
            </el-table-column>
          </template>
        </el-table>
      </template>
    </el-table-column>

    <el-table-column prop="source" label="风险来源" min-width="350" align="center" :formatter="(row) => sourceMap[row.source]" />

    <el-table-column prop="sumScore" label="分值(最高)" min-width="100" align="center" />
  </el-table>
</template>

<script setup>
import { ref, onMounted, watch } from 'vue'
import { newRequest } from '@/util/http'
import { list2map } from '@safety/shared-utils'
import { getArchMap } from '@/util/arch'
import { UserInfoPopover } from '@safety/shared-components'

const props = defineProps({
  mapId: {
    type: Number
  },
  tagId: {
    type: Number
  }
})

const tableData = ref([])
const area = ref(null)
const userMap = ref({})
const archMap = ref({})
const sourceMap = {
  0: '常规排查过期未排查',
  1: '风险评估排查过期未排查',
  2: '风险评估培训过期未培训',
  3: '安全隐患发现',
  4: '作业票',
  5: '特种设备到期未检',
  6: '证书过期',
  7: '仪表数据不在安全范围内',
  8: '发现人员违章',
  9: '监控发现人员违章',
  10: '特种设备到期未检警告',
  11: '安全附件到期未检',
  12: '安全附件到期未检警告'
}

const ticketTypeMap = {
  0: { name: '一般作业票', url: 'ticket/job/regular/look' },
  1: { name: '特殊作业票', url: 'ticket/job/special/look' },
  2: { name: '其他票据', url: 'ticket/regular/look' }
}

watch([() => props.mapId, () => props.tagId], () => {
  newRequest({
    url: 'dashboard/risk-score',
    query: { mapId: props.mapId, tagId: props.tagId },
    loading: false,
    success: (data) => {
      area.value = data.area
      tableData.value = processList(data.scores)
    }
  })
})

onMounted(() => {
  newRequest({
    url: 'dashboard/risk-score',
    query: { mapId: props.mapId, tagId: props.tagId, init: true },
    loading: false,
    success: (data) => {
      userMap.value = list2map(data.users)
      archMap.value = getArchMap(data.archTree, false)
      area.value = data.area
      tableData.value = processList(data.scores)
    }
  })
})

function processList(scores) {
  const list = scores.sort((a, b) => {
    //先按来源类型排序，再按分值降序
    const r1 = a.source - b.source
    if (r1 === 0) return b.score - a.score
    return r1
  })
  const aggList = []
  for (const row of list) {
    let group = aggList.find((it) => it.source === row.source)
    if (!group) {
      aggList.push(row)
      row.family = []
      group = row
    }
    const { sourceId, score, param } = row
    group.family.push({ sourceId, score, ...JSON.parse(param) })
  }
  aggList.forEach((it) => {
    it.sumScore = it.family.reduce((acc, cur) => Math.max(acc, cur.score), 0)
  })
  return aggList
}
</script>

<style lang="scss" scoped>
.delim {
  margin-right: 3px;
}
</style>
